import { API } from "aws-amplify";
import axios from "axios";

// API layer for the AI reply agent. Shapes follow the v2 (Tavus) contract.

export type DraftStatus =
  | "pending"
  | "approved"
  | "sending"
  | "sent"
  | "rejected"
  | "failed"
  | "skipped";
export type VideoStatus = "none" | "queued" | "generating" | "ready" | "failed";

export interface DraftVideo {
  status: VideoStatus;
  progress: string | null;
  avatarId: string | null;
  script: string | null;
  stale: boolean;
  watchUrl: string | null;
  playbackUrl: string | null;
  thumbnailUrl: string | null;
  error: string | null;
}

export interface DraftEvent {
  at: string;
  type: string;
  message: string;
}

export interface Draft {
  id: string;
  inboundMessageId: string;
  threadId: string;
  toEmail: string;
  subject: string;
  status: DraftStatus;
  decision: "reply" | "no_reply" | "escalate";
  body: string;
  editedBody: string | null;
  reason: string | null;
  classification: string | null;
  confidence: number | null;
  autoSendEligible: boolean;
  autoSent: boolean;
  error: string | null;
  decidedAt: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
  video: DraftVideo | null;
  events: DraftEvent[];
}

export interface InboxMessage {
  id: string;
  fromName?: string;
  fromEmail: string;
  subject?: string;
  snippet?: string;
  bodyText?: string;
  receivedAt?: string;
  classification?: string;
  draft: Draft | null;
  draftError: string | null;
  // True while a draft is being written in the background (202 from generate).
  draftPending?: boolean;
}

export interface Avatar {
  id: string;
  name: string;
  source: "stock" | "imported" | "video";
  status: "training" | "ready" | "failed";
  isDefault: boolean;
  thumbnailUrl: string | null;
  previewVideoUrl: string | null;
  error: string | null;
  createdAt: string;
}

export interface StockFace {
  faceId: string;
  name: string;
  thumbnailUrl: string | null;
  previewVideoUrl: string | null;
  tags: string[];
}

export interface MailboxWatch {
  connected: boolean;
  active: boolean;
  emailAddress: string | null;
  expiresAt: string | null;
}

export type CreateAvatarPayload =
  | { source: "stock"; faceId: string; name?: string }
  | { source: "video"; name: string; s3Key: string; consent: true };

const isLocalAuth = process.env.REACT_APP_LOCAL_AUTH === "true";
const baseURL = process.env.REACT_APP_APIURL;

const call = async (
  method: "get" | "post" | "put" | "patch" | "del",
  path: string,
  body?: any,
  params?: any
) => {
  if (isLocalAuth) {
    const response = await axios({
      method: method === "del" ? "delete" : method,
      url: `${baseURL}${path}`,
      data: body,
      params,
    });
    return response.data;
  }
  if (method === "get") return API.get("Backend", path, params ? { queryStringParameters: params } : {});
  if (method === "del") return API.del("Backend", path, params ? { queryStringParameters: params } : {});
  if (method === "post") return API.post("Backend", path, body ? { body } : {});
  if (method === "put") return API.put("Backend", path, body ? { body } : {});
  return API.patch("Backend", path, body ? { body } : {});
};

const enc = encodeURIComponent;

// Agent
export const fetchAgent = () => call("get", "/ai/agent");
export const saveAgent = (payload: any) => call("put", "/ai/agent", payload);
export const saveAgentExample = (payload: { situation: string; preferredResponse: string }) =>
  call("post", "/ai/agent/examples", payload);
export const runAgentTest = (payload: { subject: string; body: string }) =>
  call("post", "/ai/agent/test", payload);

// Inbox + drafts (every draft endpoint returns { draft })
export const fetchInboxMessages = () => call("get", "/ai/inbox/messages");
export const fetchDraftStats = () => call("get", "/ai/drafts/stats");
// 200 { draft, reused: true } for an existing draft, or 202 { status: "queued", inboundMessageId }.
export const requestDraftGeneration = (payload: { inboundMessageId: string; force?: boolean }) =>
  call("post", "/ai/drafts/generate", payload);
export const fetchDraft = (id: string) => call("get", `/ai/drafts/${enc(id)}`);
export const patchDraft = (id: string, body: string) => call("patch", `/ai/drafts/${enc(id)}`, { body });
export const postDraftDecision = (id: string, action: "approve" | "reject", body?: string) =>
  call("post", `/ai/drafts/${enc(id)}/decide`, body === undefined ? { action } : { action, body });
export const postDraftSend = (id: string) => call("post", `/ai/drafts/${enc(id)}/send`);
export const postDraftVideo = (id: string, avatarId?: string) =>
  call("post", `/ai/drafts/${enc(id)}/video`, avatarId ? { avatarId } : {});

// Live mailbox sync
export const fetchMailboxWatch = () => call("get", "/ai/inbox/watch");
export const startMailboxWatch = () => call("post", "/ai/inbox/watch");
export const stopMailboxWatch = () => call("del", "/ai/inbox/watch");

// Avatars (Tavus faces)
export const fetchAvatars = () => call("get", "/ai/avatars");
export const fetchStockFaces = () => call("get", "/ai/avatars/stock");
export const postAvatar = (payload: CreateAvatarPayload) => call("post", "/ai/avatars", payload);
export const fetchAvatar = (id: string) => call("get", `/ai/avatars/${enc(id)}`);
export const postDefaultAvatar = (id: string) => call("post", `/ai/avatars/${enc(id)}/default`);
export const removeAvatar = (id: string) => call("del", `/ai/avatars/${enc(id)}`);
